import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { compose, withHandlers, withState } from 'recompose';
import { withAppContext, getToken } from '@ctsu/loader';
import atoms from 'ctsutheme/components/atoms';
import { Grid } from '@material-ui/core';
import _ from 'lodash';
import { RenderEditable, RenderControl, RenderElement } from 'components/RenderHelpers';
import {
  getCtepStudyAgentDetails,
  getNonCtepStudyAgentDetails,
  STUDY_AGENT_ACTIONS,
  saveNonCTEPStudyAgents,
  cancelStudyAgent
} from 'actions/protocols/studyagents';
import {
  CanUserAccessAgents,
  CanUserAccessAgentsCtep,
  CanUserAccessAgentsNonCtep,
  CanUserAccessAgentsDevice,
  CanUserEditAgentsIND
} from 'appconstants/Permission';
import * as selectors from 'selectors';
import { bindLogger } from 'helpers/BindLogger';
import { useCtxParseValue, GetFromCtxValue } from 'helpers';
import { openConfirmation } from 'helpers/Confirmation';
import { AppStatusEnum, httpStatusEnum } from 'appconstants';
import { openSnackbar } from 'helpers/Notifier';
import { map } from 'rxjs/operators';
import { FormViewContent } from 'components/FormViewContent';
import { BoolToString } from 'components';
import { FasSave } from 'assets/icons';
import {
  AgentSelector,
  TreatmentAssignments,
  INDAgents,
  IDEAgents,
  CTEPAgents
} from './components';

const { Button, Icon, Typography } = atoms;

const loadingCTEPAgents = selectors.createLoadingSelector([
  STUDY_AGENT_ACTIONS.GET_CTEP_STUDY_AGENTS
]);
const errorCTEPAgents = selectors.createErrorMessageSelector([
  STUDY_AGENT_ACTIONS.GET_CTEP_STUDY_AGENTS
]);
const loadingNonCTEPAgents = selectors.createLoadingSelector([
  STUDY_AGENT_ACTIONS.GET_NON_CTEP_STUDY_AGENTS
]);
const errorNonCTEPAgents = selectors.createErrorMessageSelector([
  STUDY_AGENT_ACTIONS.GET_NON_CTEP_STUDY_AGENTS
]);

const IDE = (props) => (
  <IDEAgents {...props} />
);

const IDEEXEMPT = (props) => (
  <IDEAgents {...props} />
);

const SwitchAgentValue = (type) => ({
  'CTEP HELD IND': 'CTEP_IND',
  IDE: 'IDE',
  'IDE EXEMPT': 'IDE_EXEMPT',
  'IND EXEMPT': 'IND_EXEMPT',
  'NO AGENT': 'NO_AGENT',
  'NON-CTEP HELD IND': 'IND'
})[type];

const StudyAgent = ({
  context,
  protocolNumber,
  indIdeType,
  protocolId,
  nonCtep,
  ctepStudyAgentData,
  onGetCtepStudyAgentDetails,
  handleAgentChange,
  state,
  setState,
  saveIndInfo,
  saveAgentType,
  cancelAgent,
  onGetDetails,
  edit,
  toggleEdit,
  isfdaRegTrail,
  isLoadingPrtDetails,
  iserrorPrtDetails,
  isloadingCTEPAgents,
  iserrorCTEPAgents,
  isloadingNonCTEPAgents,
  iserrorNonCTEPAgents
}) => {
  const { protocolAgentGroup } = useCtxParseValue('LOV_AGENT_GROUP');
  const { agentIndIdeType } = useCtxParseValue('LOV_AGENT_IND_IDE_TYPE');
  const agentValue = SwitchAgentValue(state);

  useEffect(() => {
    if (!_.isEmpty(protocolNumber)) {
      setState(indIdeType);
    }
  }, [indIdeType, setState, protocolNumber]);

  useEffect(() => {
    if (!_.isEmpty(protocolNumber)) {
      bindLogger(onGetCtepStudyAgentDetails, context)(protocolNumber);
    }
  }, [protocolNumber, context, onGetCtepStudyAgentDetails]);

  useEffect(() => {
    if (!_.isEmpty(protocolNumber) && agentValue && agentValue.match(/^(IDE|IDE_EXEMPT|IND)$/)) {
      bindLogger(onGetDetails, context)(protocolNumber, agentValue);
    }
  }, [indIdeType, onGetDetails, context, protocolNumber, agentValue]);


  const showAgentDrpDwn = CanUserEditAgentsIND() && edit;
  const showSaveButton = agentValue && ['NO_AGENT', 'IND_EXEMPT', 'CTEP_IND'].includes(agentValue);

  return (
    <RenderControl
      isLoading={isLoadingPrtDetails}
      iserror={iserrorPrtDetails}
      compName="Study Agent Information"
      permission={CanUserAccessAgents()}
    >
      <Grid container alignItems="center" spacing={4}>
        <Grid
          item
          xs={6}
        >
          <RenderEditable
            state={edit}
            editable={CanUserEditAgentsIND()}
            setState={toggleEdit}
            title="Study Agent Information"
          >
            <FormViewContent
              primaryText="IND / IDE Type"
              secondaryText={(
                <>
                  <RenderElement access={showAgentDrpDwn}>
                    <Typography primary inline style={{ flex: '1' }}>
                      <AgentSelector
                        agentValue={agentValue}
                        options={agentIndIdeType}
                        onAgentChange={handleAgentChange}
                        selectStyles={{ minWidth: '10rem', paddingRight: '2rem' }}
                      />
                    </Typography>
                  </RenderElement>
                  <RenderElement access={!showAgentDrpDwn}>
                    <Typography data-id="stateCheck" inline>
                      {state}
                    </Typography>
                  </RenderElement>
                  <RenderElement access={showSaveButton}>
                     &nbsp;  &nbsp;
                    <Button
                      variant="outlined"
                      color="primary"
                      data-id="saveStatus"
                      name="saveStatus"
                      className={state === indIdeType ? 'disabled' : 'turquoise'}
                      disabled={state === indIdeType}
                      onClick={() => saveAgentType()}
                    >
                      <Icon className="mr-1 mt-n2">
                        <FasSave />
                      </Icon>
                      Save Changes
                    </Button>
                  </RenderElement>
                </>
)}
            />
          </RenderEditable>
        </Grid>
        <Grid item xs={6}>
          <FormViewContent
            name="isfdaRegTrail"
            primaryText=" FDA Registration Trial?"
            secondaryText={<BoolToString boolValue={isfdaRegTrail} />}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={8}>
        <Grid item xs={12}>
          <RenderElement access={['IDE', 'IDE_EXEMPT'].includes(agentValue)}>
            <RenderControl
              isLoading={isloadingNonCTEPAgents}
              iserror={iserrorNonCTEPAgents}
              compName="Agents Device(s)"
              permission={CanUserAccessAgentsDevice()}
            >
              <RenderElement access={agentValue === 'IDE'}>
                <IDE
                  data={nonCtep.data}
                  onSave={saveIndInfo}
                  protocolId={protocolId}
                  agentValue={agentValue}
                  agentLabel={state}
                  cancelAgent={cancelAgent}
                />
              </RenderElement>
              <RenderElement access={agentValue === 'IDE_EXEMPT'}>
                <IDEEXEMPT
                  data={nonCtep.data}
                  onSave={saveIndInfo}
                  protocolId={protocolId}
                  agentValue={agentValue}
                  agentLabel={state}
                  cancelAgent={cancelAgent}
                />
              </RenderElement>
            </RenderControl>
          </RenderElement>
        </Grid>
        <Grid item xs={12}>
          <RenderElement access={agentValue === 'IND'}>
            <RenderControl
              isLoading={isloadingNonCTEPAgents}
              iserror={iserrorNonCTEPAgents}
              compName="IND Agents"
              permission={CanUserAccessAgentsNonCtep()}
            >
              <INDAgents
                data={nonCtep.data}
                onSave={saveIndInfo}
                agentValue={agentValue}
                agentLabel={state}
                protocolId={protocolId}
                ctepAgents={ctepStudyAgentData.data}
                protocolAgentGroup={protocolAgentGroup}
                cancelAgent={cancelAgent}
              />
            </RenderControl>
          </RenderElement>
        </Grid>
        <Grid item xs={12}>
          <RenderControl
            isLoading={isloadingCTEPAgents}
            iserror={iserrorCTEPAgents}
            compName="CTEP Agent(s)"
            permission={CanUserAccessAgentsCtep()}
          >
            <CTEPAgents data={ctepStudyAgentData.data} />
          </RenderControl>
        </Grid>
        <Grid item xs={12}>
          <RenderControl
            isLoading={false}
            iserror={false}
            compName="Treatment Assignment(s)"
            permission={CanUserAccessAgentsCtep()}
          >
            <TreatmentAssignments
              getToken={getToken}
              protocolNumber={protocolNumber}
            />
          </RenderControl>
        </Grid>
      </Grid>
    </RenderControl>
  );
};

const withConnectState = connect(
  (state, ownProps) => ({
    indIdeType: state.prtDetails.prtInfo.indIdeType,
    isfdaRegTrail: state.prtDetails.prtInfo.isfdaRegTrail,
    ctepStudyAgentData: state.studyAgents.ctepStudyAgents,
    nonCtep: state.studyAgents.nonCTEP,
    isloadingCTEPAgents: loadingCTEPAgents(state),
    iserrorCTEPAgents: errorCTEPAgents(state),
    isloadingNonCTEPAgents: loadingNonCTEPAgents(state),
    iserrorNonCTEPAgents: errorNonCTEPAgents(state),
    protocolId: selectors.ProtocolIdSelector(state),
    protocolNumber: selectors.ProtocolNumSelector(state),
    isLoadingPrtDetails: selectors.lSelectorGetProtDetails(state),
    iserrorPrtDetails: selectors.eSelectorGetProtDetails(state)
  }),
  dispatch => bindActionCreators(
    {
      onGetCtepStudyAgentDetails: getCtepStudyAgentDetails,
      onGetDetails: getNonCtepStudyAgentDetails,
      onSaveAgents: saveNonCTEPStudyAgents,
      onCancelAgent: cancelStudyAgent
    },
    dispatch
  )
);

const checkNonCtep = data => _.find(data, ['esysStatus', 'ACTIVE']);

const StudyAgentContainer = compose(
  withAppContext,
  withConnectState,
  withState('state', 'setState', null),
  withState('edit', 'setEdit', false),
  withHandlers({
    toggleEdit: props => event => {
      const {
        edit, setState, setEdit, indIdeType
      } = props;
      setEdit(!edit);
      setState(indIdeType);
    },
    handleAgentChange: props => event => {
      const {
        state,
        setState,
        context,
        nonCtep
      } = props;
      const agentValue = SwitchAgentValue(state);
      const UpdateState = () => {
        setState(event.currentTarget.innerText.toUpperCase());
      };
      if (agentValue !== event.target.value) {
        if (agentValue && agentValue.match(/^(IDE|IDE_EXEMPT|IND)$/)) {
          if (!checkNonCtep(nonCtep.data)) {
            UpdateState();
          } else {
            openConfirmation({
              message: GetFromCtxValue(context, 'IND_IDE_ACTIVE_CHECK').replace(/agentType/g, state),
              onAction: () => {},
              name: 'ind_ide_active'
            });
          }
        } else {
          UpdateState();
        }
      } else {
        UpdateState();
      }
    },
    saveIndInfo: props => data => {
      const {
        onSaveAgents,
        context,
        protocolId
      } = props;
      return bindLogger(onSaveAgents, context)(protocolId, data).pipe(
        map(resp => {
          if (resp.status === httpStatusEnum.SUCCESS) {
            openSnackbar({
              message: GetFromCtxValue(context, 'SAVE_STUDY_AGENTS_SUCCESSFUL'),
              variant: 'success',
              name: 'study_agents'
            });
          }
          return resp;
        })
      );
    },
    saveAgentType: props => event => {
      const {
        onSaveAgents, context, protocolId, state
      } = props;
      const agentValue = SwitchAgentValue(state);

      const confirmSave = () => {
        bindLogger(onSaveAgents, context)(protocolId, {
          esysStatus: AppStatusEnum.ACTIVE,
          indIdeType: state,
          prtId: protocolId
        }).subscribe(resp => {
          if (resp.status === httpStatusEnum.SUCCESS) {
            openSnackbar({
              message: GetFromCtxValue(context, 'SAVE_STUDY_AGENTS_SUCCESSFUL'),
              variant: 'success',
              name: 'study_agents'
            });
          }
        });
      };

      const SwitchAgentMessage = (type) => ({
        IND_EXEMPT: GetFromCtxValue(context, 'SAVE_IND_EXEMPT'),
        NO_AGENT: GetFromCtxValue(context, 'SAVE_NO_AGENT'),
        CTEP_IND: GetFromCtxValue(context, 'SAVE_CTEP_HELD_IND')
      })[type];

      openConfirmation({
        message: SwitchAgentMessage(agentValue),
        onAction: confirmSave,
        name: 'switch_agent'
      });
    },
    cancelAgent: props => (id, type) => {
      const {
        onCancelAgent, context, protocolId
      } = props;
      const confirmDelete = () => {
        bindLogger(onCancelAgent, context)(protocolId, id, type).subscribe(resp => {
          if (resp.status === httpStatusEnum.SUCCESS) {
            openSnackbar({
              message: GetFromCtxValue(context, 'CANCEL_STUDY_AGENT_SUCCESS'),
              variant: 'success',
              name: 'study_agents_cancel'
            });
          }
        });
      };
      openConfirmation({
        message: GetFromCtxValue(context, 'CANCEL_STUDY_AGENT_CONFIRM'),
        onAction: confirmDelete,
        name: 'delete_agent'
      });
    }
  })
)(StudyAgent);

export default StudyAgentContainer;
